import { createFileRoute } from '@tanstack/react-router'
import { LegalDoc } from '@turfchale/ui'

import { Logo } from '@/components/ui/Logo'

export const PLAYER_AGREEMENT_VERSION = '1.0'
const EFFECTIVE_DATE = '20 June 2026'

export const Route = createFileRoute('/legal/player-agreement')({
  component: PlayerAgreementPage,
})

function Header() {
  return (
    <header
      style={{
        alignItems: 'center',
        background: 'var(--bg2)',
        borderBottom: '1px solid var(--lime-line)',
        display: 'flex',
        justifyContent: 'center',
        padding: '14px 16px',
      }}
    >
      <Logo fontSize={18} />
    </header>
  )
}

function PlayerAgreementPage() {
  return (
    <LegalDoc
      effectiveDate={EFFECTIVE_DATE}
      header={<Header />}
      intro={
        <>
          <p>
            This Player Agreement (&ldquo;Agreement&rdquo;) is a contract between you
            (&ldquo;Player&rdquo;, &ldquo;you&rdquo;) and TurfChale (&ldquo;TurfChale&rdquo;,
            &ldquo;we&rdquo;, &ldquo;us&rdquo;) governing your use of the TurfChale platform to
            discover and book sports turfs. It supplements our <a href="/terms">Terms of Service</a>{' '}
            and <a href="/privacy">Privacy Policy</a>.
          </p>
          <div className="legal-note">
            By creating an account, confirming a booking, or otherwise using TurfChale, you accept
            this Agreement. If you do not agree, do not use the platform.
          </div>
        </>
      }
      title="Player Agreement"
      version={PLAYER_AGREEMENT_VERSION}
    >
      <h2>1. Acceptance and eligibility</h2>
      <ul>
        <li>You must be at least 18 years old to create an account and make bookings.</li>
        <li>You agree to provide accurate, current, and complete information.</li>
        <li>You are responsible for all activity that occurs under your account.</li>
        <li>One person may not maintain multiple accounts.</li>
      </ul>

      <h2>2. The TurfChale platform</h2>
      <p>
        TurfChale is a marketplace that connects players with turf owners. TurfChale facilitates
        discovery, booking, and payment, but does not own, operate, or control any turf venue. The
        contract for use of a venue is between you and the turf owner.
      </p>

      <h2>3. Bookings and payments</h2>
      <ul>
        <li>A booking is confirmed only on successful payment through our payment provider.</li>
        <li>
          Slot availability is shown in real time but is not guaranteed until your payment
          completes. Held slots are released if not paid within the hold window.
        </li>
        <li>TurfChale is free for players; you pay the turf owner&rsquo;s published rate.</li>
        <li>A refundable deposit may apply to some venues and will be shown before you confirm.</li>
      </ul>

      <h2>4. Cancellations and refunds</h2>
      <p>
        Cancellations and refunds are governed by our <a href="/refund">Refund Policy</a> and the
        individual turf owner&rsquo;s cancellation terms shown before you confirm. In summary, you
        may cancel up to 2 hours before the slot start time, and refund eligibility depends on the
        owner&rsquo;s policy.
      </p>

      <h2>5. Player conduct and venue rules</h2>
      <ul>
        <li>You must arrive on time and vacate the venue at the end of your booked slot.</li>
        <li>You must follow all rules, signage, and instructions set by the turf owner.</li>
        <li>You are liable for any damage you or your group cause to the venue.</li>
        <li>
          Bookings are personal and non-transferable without the owner&rsquo;s written consent.
        </li>
        <li>You must not behave abusively toward staff, other players, or owners.</li>
      </ul>

      <h2>6. Assumption of risk</h2>
      <p>
        Sport carries inherent risks of physical injury. You participate voluntarily and at your own
        risk. To the maximum extent permitted by law, you assume all risk of injury, loss, or damage
        arising from your use of any venue or participation in any activity, and you release
        TurfChale from liability for such injury, loss, or damage. TurfChale does not inspect,
        certify, or guarantee the safety of any venue.
      </p>

      <h2>7. Reviews and user content</h2>
      <p>
        You may post reviews and ratings that are honest, lawful, and based on genuine experience.
        You grant TurfChale a non-exclusive, royalty-free licence to display content you submit. We
        may remove content that is false, abusive, or violates these terms.
      </p>

      <h2>8. Prohibited conduct</h2>
      <ul>
        <li>Using the platform for any unlawful purpose.</li>
        <li>Arranging off-platform payments to circumvent the booking system.</li>
        <li>Posting fraudulent listings, reviews, or claims.</li>
        <li>Scraping, crawling, or extracting data, or disrupting the service.</li>
        <li>Impersonating another person or entity.</li>
      </ul>

      <h2>9. Suspension and termination</h2>
      <p>
        We may suspend or terminate your account for breach of this Agreement, fraud, abuse, or
        repeated no-shows. You may stop using the platform at any time.
      </p>

      <h2>10. Disclaimers and limitation of liability</h2>
      <p>
        The platform is provided &ldquo;as is&rdquo;. TurfChale is not responsible for the quality,
        safety, or availability of any venue, for any injury or loss arising from venue use, or for
        disputes between players and owners. To the maximum extent permitted by law,
        TurfChale&rsquo;s total liability for any claim arising from your use of the service is
        limited to the amount you paid for the specific booking in dispute.
      </p>

      <h2>11. Governing law and disputes</h2>
      <p>
        This Agreement is governed by the laws of India. Disputes shall first be raised with
        TurfChale support; if unresolved, they are subject to the exclusive jurisdiction of the
        courts of Mumbai, Maharashtra.
      </p>

      <h2>12. Changes to this Agreement</h2>
      <p>
        We may update this Agreement. Material changes will be communicated by email or in-app
        notice at least 15 days before they take effect. Continued use after the effective date
        constitutes acceptance.
      </p>

      <h2>13. Contact</h2>
      <p>
        Questions: <a href="mailto:support@turfchale.com">support@turfchale.com</a>. Legal notices:{' '}
        <a href="mailto:legal@turfchale.com">legal@turfchale.com</a>.
      </p>
    </LegalDoc>
  )
}
